import type { CmtsApiErrorCatalogEntry, CmtsKnownBackendErrorCode } from "./cmtsApiErrorTypes.js";

export const CMTS_KNOWN_BACKEND_ERROR_CODES = [
  "invalid_payload",
  "validation_failed",
  "missing_entity",
  "duplicate_business_key",
  "missing_reference_value",
  "inactive_reference_used",
  "cross_tenant_mismatch",
  "cross_org_mismatch",
  "dependency_exists",
  "deactivation_blocked",
  "invalid_workflow_transition",
  "unsupported_workflow_action",
  "rbac_blocked",
  "sod_blocked",
  "evidence_missing",
  "evidence_rejected",
  "stale_snapshot",
  "concurrency_conflict",
  "idempotency_conflict",
  "persistence_failure",
] as const satisfies readonly CmtsKnownBackendErrorCode[];

export const cmtsApiErrorCatalog: Record<CmtsKnownBackendErrorCode, CmtsApiErrorCatalogEntry> = {
  invalid_payload: {
    code: "invalid_payload",
    httpStatus: "400",
    category: "validation",
    severity: "error",
    uiComponent: "ApiErrorPanel",
    fieldErrorSupport: true,
    dependencyErrorSupport: false,
    retryable: false,
    defaultMessage: "The request payload is invalid.",
    remediation: "Review the submitted values and correct invalid fields.",
  },
  validation_failed: {
    code: "validation_failed",
    httpStatus: "400/422",
    category: "field_validation",
    severity: "error",
    uiComponent: "FieldErrors",
    fieldErrorSupport: true,
    dependencyErrorSupport: false,
    retryable: false,
    defaultMessage: "Validation failed.",
    remediation: "Correct highlighted fields and submit again.",
  },
  missing_entity: {
    code: "missing_entity",
    httpStatus: "404",
    category: "route",
    severity: "warning",
    uiComponent: "SwotMissingEntity",
    fieldErrorSupport: false,
    dependencyErrorSupport: false,
    retryable: false,
    defaultMessage: "The requested record was not found.",
    remediation: "Refresh the list or return to the previous page.",
  },
  duplicate_business_key: {
    code: "duplicate_business_key",
    httpStatus: "409",
    category: "validation",
    severity: "error",
    uiComponent: "FieldErrors",
    fieldErrorSupport: true,
    dependencyErrorSupport: false,
    retryable: false,
    defaultMessage: "A record with the same business key already exists for this tenant and scope.",
    remediation: "Use a unique code/reference for this tenant and scope.",
  },
  missing_reference_value: {
    code: "missing_reference_value",
    httpStatus: "400/422",
    category: "reference",
    severity: "error",
    uiComponent: "FieldErrors",
    fieldErrorSupport: true,
    dependencyErrorSupport: false,
    retryable: false,
    defaultMessage: "A required reference value is missing or unavailable.",
    remediation: "Select a valid active reference value.",
  },
  inactive_reference_used: {
    code: "inactive_reference_used",
    httpStatus: "400/409",
    category: "reference",
    severity: "blocking",
    uiComponent: "ApiErrorPanel",
    fieldErrorSupport: true,
    dependencyErrorSupport: false,
    retryable: false,
    defaultMessage: "An inactive reference value was selected.",
    remediation: "Activate the referenced record or select another active value.",
  },
  cross_tenant_mismatch: {
    code: "cross_tenant_mismatch",
    httpStatus: "403/409",
    category: "authorization",
    severity: "blocking",
    uiComponent: "SwotPermissionDenied",
    fieldErrorSupport: false,
    dependencyErrorSupport: false,
    retryable: false,
    defaultMessage: "The selected record belongs to a different tenant scope.",
    remediation: "Switch to the correct tenant context or contact an administrator.",
  },
  cross_org_mismatch: {
    code: "cross_org_mismatch",
    httpStatus: "403/409",
    category: "authorization",
    severity: "blocking",
    uiComponent: "SwotPermissionDenied",
    fieldErrorSupport: false,
    dependencyErrorSupport: false,
    retryable: false,
    defaultMessage: "The selected record is outside your organization scope.",
    remediation: "Select an entity within your organization scope.",
  },
  dependency_exists: {
    code: "dependency_exists",
    httpStatus: "409",
    category: "dependency",
    severity: "blocking",
    uiComponent: "DependencyErrors",
    fieldErrorSupport: false,
    dependencyErrorSupport: true,
    retryable: false,
    defaultMessage: "Dependent records exist and block this action.",
    remediation: "Remove or resolve dependent records before continuing.",
  },
  deactivation_blocked: {
    code: "deactivation_blocked",
    httpStatus: "409",
    category: "dependency",
    severity: "blocking",
    uiComponent: "DependencyErrors",
    fieldErrorSupport: false,
    dependencyErrorSupport: true,
    retryable: false,
    defaultMessage: "This record cannot be deactivated while active dependencies exist.",
    remediation: "Resolve active dependencies before deactivation.",
  },
  invalid_workflow_transition: {
    code: "invalid_workflow_transition",
    httpStatus: "409",
    category: "workflow",
    severity: "blocking",
    uiComponent: "SwotConflictState",
    fieldErrorSupport: false,
    dependencyErrorSupport: false,
    retryable: true,
    defaultMessage: "The workflow transition is no longer valid for the current state.",
    remediation: "Refresh allowed actions and retry only if the action remains available.",
  },
  unsupported_workflow_action: {
    code: "unsupported_workflow_action",
    httpStatus: "400/409",
    category: "workflow",
    severity: "blocking",
    uiComponent: "SwotConflictState",
    fieldErrorSupport: false,
    dependencyErrorSupport: false,
    retryable: false,
    defaultMessage: "The selected workflow action is not supported.",
    remediation: "Use one of the backend-provided allowed actions.",
  },
  rbac_blocked: {
    code: "rbac_blocked",
    httpStatus: "403",
    category: "authorization",
    severity: "blocking",
    uiComponent: "SwotPermissionDenied",
    fieldErrorSupport: false,
    dependencyErrorSupport: false,
    retryable: false,
    defaultMessage: "You do not have permission to perform this action.",
    remediation: "Request the required permission or switch to an authorized user.",
  },
  sod_blocked: {
    code: "sod_blocked",
    httpStatus: "403/409",
    category: "segregation_of_duties",
    severity: "blocking",
    uiComponent: "SwotPermissionDenied",
    fieldErrorSupport: false,
    dependencyErrorSupport: false,
    retryable: false,
    defaultMessage: "Segregation of duties blocked this action. A different authorized user must complete this step.",
    remediation: "Assign a different authorized user to complete this step.",
  },
  evidence_missing: {
    code: "evidence_missing",
    httpStatus: "404/409",
    category: "evidence",
    severity: "error",
    uiComponent: "ApiErrorPanel",
    fieldErrorSupport: false,
    dependencyErrorSupport: false,
    retryable: true,
    defaultMessage: "Required evidence is missing.",
    remediation: "Refresh the evidence list or upload the required evidence.",
  },
  evidence_rejected: {
    code: "evidence_rejected",
    httpStatus: "400/422/409",
    category: "evidence",
    severity: "blocking",
    uiComponent: "ApiErrorPanel",
    fieldErrorSupport: false,
    dependencyErrorSupport: false,
    retryable: false,
    defaultMessage: "Evidence was rejected by backend validation.",
    remediation: "Review file type, size, confidentiality, scan result, and upload policy.",
  },
  stale_snapshot: {
    code: "stale_snapshot",
    httpStatus: "409",
    category: "concurrency",
    severity: "warning",
    uiComponent: "SwotConflictState",
    fieldErrorSupport: false,
    dependencyErrorSupport: false,
    retryable: true,
    defaultMessage: "This record was updated by another user.",
    remediation: "Refresh the record and retry with the latest version.",
  },
  concurrency_conflict: {
    code: "concurrency_conflict",
    httpStatus: "409",
    category: "concurrency",
    severity: "warning",
    uiComponent: "SwotConflictState",
    fieldErrorSupport: false,
    dependencyErrorSupport: false,
    retryable: true,
    defaultMessage: "A concurrency conflict occurred.",
    remediation: "Reload the latest record before saving again.",
  },
  idempotency_conflict: {
    code: "idempotency_conflict",
    httpStatus: "409",
    category: "idempotency",
    severity: "warning",
    uiComponent: "SwotConflictState",
    fieldErrorSupport: false,
    dependencyErrorSupport: false,
    retryable: false,
    defaultMessage: "A duplicate idempotent submission was detected.",
    remediation: "Check whether the previous request already succeeded before retrying.",
  },
  persistence_failure: {
    code: "persistence_failure",
    httpStatus: "500",
    category: "persistence",
    severity: "error",
    uiComponent: "SwotRetryPanel",
    fieldErrorSupport: false,
    dependencyErrorSupport: false,
    retryable: true,
    defaultMessage: "The backend could not persist the request.",
    remediation: "Retry later or contact support with correlation ID.",
  },
};

export function isKnownCmtsBackendErrorCode(value: unknown): value is CmtsKnownBackendErrorCode {
  return typeof value === "string" && Object.prototype.hasOwnProperty.call(cmtsApiErrorCatalog, value);
}

export function catalogEntryForErrorCode(code: string): CmtsApiErrorCatalogEntry | undefined {
  return isKnownCmtsBackendErrorCode(code) ? cmtsApiErrorCatalog[code] : undefined;
}
